/**
 * Application validation utilities
 * Provides reusable validation functions for application-related data
 */

import { ConfigHelper } from '@devxdiscover/devhub-common';

/**
 * Result of application name uniqueness check
 */
export interface NameUniquenessResult {
  isUnique: boolean;
  error?: string;
}

/**
 * Checks if an application name is unique across all statuses
 * @param name - The application name to check
 * @returns Promise resolving to uniqueness result
 */
export async function checkApplicationNameUniqueness(
  name: string,
): Promise<NameUniquenessResult> {
  if (!name || name.trim().length === 0) {
    return { isUnique: true };
  }

  try {
    const fusionUrl = ConfigHelper.instance.apiUrl('fusion/query').href;
    const response = await fetch(fusionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        query: `
          query CheckApplicationName($name: String!) {
            resources {
              applicationsPaged(
                filter: { applicationName: $name, includeSystemTypes: [App, AiSystem] }
                pagination: { limit: 1 }
              ) {
                totalCount
                applications {
                  applicationId
                  name
                  status
                  systemType
                }
              }
            }
          }
        `,
        variables: {
          name: name.trim(),
        },
      }),
    });

    if (!response.ok) {
      return {
        isUnique: true, // Fail open - don't block if API is down
        error: 'Unable to verify name uniqueness',
      };
    }

    const result = await response.json();

    const totalCount =
      result?.data?.resources?.applicationsPaged?.totalCount ?? 0;
    const applications =
      result?.data?.resources?.applicationsPaged?.applications ?? [];

    if (totalCount > 0 && applications.length > 0) {
      const existingApp = applications[0];
      const kind =
        existingApp.systemType === 'AiSystem' ? 'AI System' : 'Application';
      const errorMsg = `Name "${name}" is already in use by an existing ${kind} (ID: ${existingApp.applicationId}, Status: ${existingApp.status})`;
      return {
        isUnique: false,
        error: errorMsg,
      };
    }

    return { isUnique: true };
  } catch (error) {
    // Fail open - don't block the form if there's an error
    return {
      isUnique: true,
      error: 'Unable to verify name uniqueness',
    };
  }
}
